<template>
  <div class="account-page" dir="rtl">

    <!-- LOGGED IN -->
    <template v-if="user">

      <!-- Profile hero -->
      <div class="acc-hero">
        <div class="acc-hero__avatar">{{ userInitial }}</div>
        <h2 class="acc-hero__name">{{ user.name }}</h2>
        <p class="acc-hero__email">{{ user.email }}</p>
        <p class="acc-hero__cheer">{{ cheerLine }}</p>
      </div>

      <!-- Points / level card -->
      <button v-if="user.points_level" class="acc-points" @click="goPoints" type="button">
        <div class="acc-points__icon">{{ user.points_level.current?.icon || "🌱" }}</div>
        <div class="acc-points__main">
          <div class="acc-points__top">
            <span class="acc-points__level">{{ user.points_level.current?.name_ar }}</span>
            <span class="acc-points__total"><b>{{ user.points ?? 0 }}</b> نقطة</span>
          </div>
          <div class="acc-points__bar">
            <div class="acc-points__bar-fill" :style="{ width: (user.points_level.next ? user.points_level.progress_pct : 100) + '%' }"></div>
          </div>
          <span class="acc-points__next">
            <template v-if="user.points_level.next">
              باقي {{ user.points_level.next.remaining }} نقطة للمستوى {{ user.points_level.next.icon }} {{ user.points_level.next.name_ar }}
            </template>
            <template v-else>أعلى مستوى 🎉</template>
          </span>
        </div>
        <i class="fa fa-chevron-left acc-points__arrow"></i>
      </button>

      <!-- Stat numbers -->
      <div class="acc-stats">
        <button class="acc-stat" @click="goContributions('experiences')" type="button">
          <span class="acc-stat__value">{{ stats.visits_count ?? 0 }}</span>
          <span class="acc-stat__label">تجربة شاركتها</span>
        </button>
        <button class="acc-stat" @click="goContributions('places')" type="button">
          <span class="acc-stat__value">{{ stats.branches_added_count ?? 0 }}</span>
          <span class="acc-stat__label">مكان أضفته</span>
        </button>
        <div class="acc-stat">
          <span class="acc-stat__value">{{ stats.unique_categories_count ?? 0 }}</span>
          <span class="acc-stat__label">تصنيف لاحظته</span>
        </div>
      </div>

      <!-- Insight chips -->
      <div v-if="stats.top_period || stats.top_category" class="acc-insights">
        <div v-if="stats.top_period" class="acc-insight">
          <span class="acc-insight__icon">{{ stats.top_period.icon }}</span>
          <span class="acc-insight__text">معظم زياراتك في <strong>{{ stats.top_period.label }}</strong></span>
        </div>
        <div v-if="stats.top_category" class="acc-insight">
          <i v-if="stats.top_category.icon" :class="stats.top_category.icon"
             :style="{ color: stats.top_category.icon_style || 'var(--color-signal)' }"></i>
          <span class="acc-insight__text">أكثر تصنيف تختاره <strong>{{ stats.top_category.name }}</strong></span>
        </div>
      </div>

      <!-- Recently rated strip -->
      <section v-if="stats.recent_menu_votes?.length" class="acc-rated">
        <div class="acc-rated__head">
          <h3 class="acc-rated__title">قيّمت مؤخراً</h3>
          <button class="acc-rated__more" @click="goContributions('experiences')" type="button">الكل ←</button>
        </div>
        <div class="acc-rated__row">
          <button
            v-for="mv in stats.recent_menu_votes"
            :key="mv.menu_vote_id"
            class="acc-rated__card"
            @click="goItem(mv)" type="button"
          >
            <div class="acc-rated__img-wrap">
              <img v-if="menuImg(mv.image)" class="acc-rated__img" :src="menuImg(mv.image)" alt="" @error="onRatedImgError" />
              <div v-else class="acc-rated__img acc-rated__img--ph">🍴</div>
            </div>
            <div class="acc-rated__info">
              <span class="acc-rated__name">{{ mv.item_name }}</span>
              <div v-if="mv.rating" class="acc-rated__stars">
                <i v-for="n in 5" :key="n" class="fa" :class="n <= mv.rating ? 'fa-star' : 'fa-star-o'"></i>
              </div>
              <span class="acc-rated__branch">{{ mv.branch_name }}</span>
            </div>
          </button>
        </div>
      </section>

      <!-- Nav rows -->
      <nav class="acc-nav">
        <button class="acc-row" @click="goProfile" type="button">
          <span class="acc-row__icon">👤</span>
          <span class="acc-row__label">الملف الشخصي</span>
          <span v-if="profileIncomplete" class="acc-row__badge acc-row__badge--warn">!</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goContributions('experiences')" type="button">
          <span class="acc-row__icon">🗺️</span>
          <span class="acc-row__label">مساهماتي</span>
          <span v-if="stats.visits_count" class="acc-row__hint">{{ stats.visits_count }}</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goLikes" type="button">
          <span class="acc-row__icon">❤️</span>
          <span class="acc-row__label">إعجاباتي</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goBookmarks" type="button">
          <span class="acc-row__icon">🔖</span>
          <span class="acc-row__label">المحفوظات</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goNotifications" type="button">
          <span class="acc-row__icon">🔔</span>
          <span class="acc-row__label">الإشعارات</span>
          <span v-if="unreadCount > 0" class="acc-row__badge">{{ unreadCount }}</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goFeedback" type="button">
          <span class="acc-row__icon">✉️</span>
          <span class="acc-row__label">تواصل معنا / اقتراح</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goPolicies" type="button">
          <span class="acc-row__icon">📄</span>
          <span class="acc-row__label">السياسات والموافقات</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
        <button class="acc-row" @click="goAbout" type="button">
          <span class="acc-row__icon">ℹ️</span>
          <span class="acc-row__label">من نحن</span>
          <i class="fa fa-chevron-left acc-row__arrow"></i>
        </button>
      </nav>

      <!-- Appearance -->
      <div class="acc-theme">
        <span class="acc-theme__label">المظهر</span>
        <div class="acc-theme__btns">
          <button class="acc-theme__btn" :class="{ 'acc-theme__btn--active': theme === 'light' }" @click="setTheme('light')" type="button">☀️ فاتح</button>
          <button class="acc-theme__btn" :class="{ 'acc-theme__btn--active': theme === 'dark' }" @click="setTheme('dark')" type="button">🌙 داكن</button>
          <button class="acc-theme__btn" :class="{ 'acc-theme__btn--active': theme === 'system' }" @click="setTheme('system')" type="button">⚙️ تلقائي</button>
        </div>
      </div>

      <!-- Logout -->
      <button class="acc-logout" @click="doLogout" :disabled="loading" type="button">
        <i class="fa fa-sign-out"></i>
        تسجيل خروج
      </button>

      <!-- Footer -->
      <div class="acc-footer">
        <span class="acc-footer__brand">وِجْهَة</span>
        <span class="acc-footer__version">الإصدار {{ appVersion }}</span>
      </div>

    </template>

    <!-- LOGGED OUT -->
    <template v-else>
      <div class="acc-auth">
        <div class="acc-auth__header">
          <h2 class="acc-auth__title">أهلاً بك في وِجْهَة</h2>
          <p class="acc-auth__subtitle">سجّل دخولك للتصويت وإضافة الأماكن</p>
        </div>
        <div class="acc-auth__toggle">
          <button class="acc-auth__toggle-btn" :class="{ 'acc-auth__toggle-btn--active': mode === 'login' }" @click="mode = 'login'" type="button">دخول</button>
          <button class="acc-auth__toggle-btn" :class="{ 'acc-auth__toggle-btn--active': mode === 'register' }" @click="mode = 'register'" type="button">حساب جديد</button>
        </div>
        <form class="acc-auth__form" @submit.prevent="mode === 'login' ? doLogin() : doRegister()">
          <div v-if="mode === 'register'" class="acc-field">
            <label class="acc-field__label">الاسم</label>
            <input class="acc-field__input" v-model.trim="form.name" type="text" placeholder="اسمك الكامل" required autocomplete="name" />
          </div>
          <div class="acc-field">
            <label class="acc-field__label">البريد الإلكتروني</label>
            <input class="acc-field__input" v-model.trim="form.email" type="email" placeholder="name@example.com" required autocomplete="email" dir="ltr" />
          </div>
          <div class="acc-field">
            <label class="acc-field__label">كلمة المرور</label>
            <input class="acc-field__input" v-model="form.password" type="password" placeholder="********" required autocomplete="current-password" />
          </div>
          <div v-if="mode === 'register'" class="acc-field">
            <label class="acc-field__label">تأكيد كلمة المرور</label>
            <input class="acc-field__input" v-model="form.password_confirmation" type="password" placeholder="********" required autocomplete="new-password" />
          </div>
          <div v-if="mode === 'register'" class="acc-field">
            <label class="acc-field__label">الجنس</label>
            <div class="acc-choice">
              <button type="button" class="acc-choice__btn" :class="{ 'acc-choice__btn--on': form.gender === 'male' }" @click="form.gender = 'male'">ذكر</button>
              <button type="button" class="acc-choice__btn" :class="{ 'acc-choice__btn--on': form.gender === 'female' }" @click="form.gender = 'female'">أنثى</button>
            </div>
          </div>
          <div v-if="mode === 'register'" class="acc-field">
            <label class="acc-field__label">تاريخ الميلاد</label>
            <input class="acc-field__input" v-model="form.date_of_birth" type="date" :max="todayStr" dir="ltr" required />
          </div>
          <p v-if="mode === 'register'" class="acc-auth__terms">
            بالتسجيل أنت توافق على
            <router-link :to="{ name: 'StaticPage', params: { slug: 'terms-of-service' } }">شروط الاستخدام</router-link>
            و
            <router-link :to="{ name: 'StaticPage', params: { slug: 'privacy-policy' } }">سياسة الخصوصية</router-link>
          </p>
          <button class="acc-auth__submit" type="submit" :disabled="loading">
            <i class="fa" :class="loading ? 'fa-spinner fa-spin' : 'fa-arrow-left'"></i>
            {{ loading ? "جاري..." : (mode === 'login' ? "تسجيل دخول" : "إنشاء حساب") }}
          </button>
        </form>
      </div>
      <AppInfoSection />
    </template>

    <!-- Error -->
    <div v-if="error" class="acc-error">
      <i class="fa fa-exclamation-circle"></i>
      {{ formattedError }}
    </div>

  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { login, register, me, logout, meStats } from "@/services/auth";
import { usePolicyStatus } from "@/composables/usePolicyStatus";
import { useTheme } from "@/composables/useTheme";
import { useNotifications } from "@/composables/useNotifications";
import AppInfoSection from "@/components/AppInfoSection.vue";

const router = useRouter();
const { theme, setTheme } = useTheme();
const { reset: resetPolicyStatus } = usePolicyStatus();
const { unreadCount, fetchUnread, reset: resetNotifications } = useNotifications();

const appVersion = process.env.VUE_APP_VERSION || "1.0.0";
const apiUrlImg  = process.env.VUE_APP_API_URL_IMAGES || "";

// Menu-vote image: raw relative path; pass through if already absolute
function menuImg(image) {
  if (!image) return null;
  if (/^https?:\/\//.test(image)) return image;
  return `${apiUrlImg}/${image}`.replace(/([^:]\/)\/+/g, "$1");
}
function onRatedImgError(e) { if (e?.target) e.target.style.display = "none"; }

const loading = ref(false);
const error   = ref(null);
const mode    = ref("login");
const user    = ref(null);
const stats   = ref({
  branches_added_count:    0,
  visits_count:            0,
  unique_categories_count: 0,
  top_category:            null,
  top_period:              null,
  recent_menu_votes:       [],
});

const form = ref({ name: "", email: "", password: "", password_confirmation: "", gender: "", date_of_birth: "" });

const todayStr = new Date().toISOString().slice(0, 10);
const profileIncomplete = computed(() => user.value?.profile_completion?.complete === false);

const userInitial = computed(() => user.value?.name?.trim()?.charAt(0) || "?");

const cheerLine = computed(() => {
  const v = stats.value.visits_count ?? 0;
  const p = stats.value.branches_added_count ?? 0;
  if (!v && !p) return "ابدأ رحلتك — شارك تجربتك في أول مكان";
  const parts = [];
  if (v) parts.push(`شاركت ${v} تجربة`);
  if (p) parts.push(`أضفت ${p} مكاناً`);
  return parts.join(" و") + " 🎉";
});

const formattedError = computed(() => {
  if (!error.value) return "";
  if (typeof error.value === "string") return error.value;
  if (error.value?.message) return error.value.message;
  if (error.value?.errors)  return Object.values(error.value.errors).flat().join(" — ");
  return "حدث خطأ، حاول مجددًا.";
});

function loadLocalUser() {
  const u = localStorage.getItem("user");
  user.value = u ? JSON.parse(u) : null;
}

function goContributions(tab) {
  router.push({ name: "MyContributions", query: tab ? { tab } : undefined });
}
function goNotifications() { router.push({ name: "Notifications" }); }
function goLikes()         { router.push({ name: "MyLikes" }); }
function goBookmarks()     { router.push({ name: "MyBookmarks" }); }
function goFeedback()      { router.push({ name: "Feedback" }); }
function goPoints()        { router.push({ name: "Points" }); }
function goProfile()       { router.push({ name: "ProfileEdit" }); }
function goPolicies()      { router.push({ name: "StaticPageIndex" }); }
function goAbout()         { router.push({ name: "StaticPage", params: { slug: "about-us" } }); }
function goItem(mv) {
  if (mv?.branch_id && mv?.item_id) {
    router.push({ name: "MenuItemDetail", params: { branchId: mv.branch_id, itemId: mv.item_id } });
  } else if (mv?.branch_id) {
    router.push(`/branch/${mv.branch_id}`);
  }
}

async function refreshMe()    { user.value  = await me(); }
async function refreshStats() {
  const s = await meStats();
  stats.value = { ...stats.value, ...s };
}

async function refreshAll() {
  loading.value = true;
  error.value   = null;
  try {
    await refreshMe();
    await refreshStats();
  } catch (e) {
    error.value = e?.response?.data || e?.message || "تعذر تحديث البيانات.";
  } finally {
    loading.value = false;
  }
}

async function doLogin() {
  loading.value = true; error.value = null;
  try {
    await login({ email: form.value.email, password: form.value.password });
    resetPolicyStatus();
    await refreshAll();
  } catch (e) {
    error.value = e?.response?.data || e?.message || "تعذر تسجيل الدخول.";
  } finally { loading.value = false; }
}

async function doRegister() {
  loading.value = true; error.value = null;
  try {
    await register({
      name:                  form.value.name,
      email:                 form.value.email,
      password:              form.value.password,
      password_confirmation: form.value.password_confirmation,
      gender:                form.value.gender || undefined,
      date_of_birth:         form.value.date_of_birth || undefined,
    });
    resetPolicyStatus();
    await refreshAll();
  } catch (e) {
    error.value = e?.response?.data || e?.message || "تعذر إنشاء الحساب.";
  } finally { loading.value = false; }
}

async function doLogout() {
  loading.value = true;
  resetPolicyStatus();
  resetNotifications();
  try { await logout(); } catch {}
  finally {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    user.value  = null;
    stats.value = { branches_added_count: 0, visits_count: 0, unique_categories_count: 0, top_category: null, top_period: null, recent_menu_votes: [] };
    loading.value = false;
  }
}

onMounted(async () => {
  loadLocalUser();
  if (localStorage.getItem("token")) {
    await refreshAll();
    fetchUnread();
  }
});
</script>

<style scoped lang="scss">
.account-page {
  background: var(--color-bg);
  padding: env(safe-area-inset-top, 0px) 0 var(--page-padding-bottom);
  direction: rtl;
  text-align: right;
  min-height: 100vh;
  min-height: 100dvh;
}

.acc-hero, .acc-stats, .acc-insights, .acc-rated, .acc-nav, .acc-theme, .acc-logout, .acc-footer, .acc-points {
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

// Points / level card
.acc-points {
  display: flex; align-items: center; gap: var(--space-3);
  width: calc(100% - var(--page-padding-x) * 2);
  margin: 0 var(--page-padding-x) var(--space-3);
  padding: var(--space-3);
  background: linear-gradient(135deg, rgba(226,182,115,0.14), rgba(226,182,115,0.04));
  border: 1px solid rgba(226,182,115,0.3);
  border-radius: var(--radius-lg);
  cursor: pointer; text-align: right; font-family: var(--font-primary);
  transition: border-color var(--transition-base);
  &:hover { border-color: var(--color-signal); }

  &__icon {
    width: 46px; height: 46px; border-radius: var(--radius-md); flex-shrink: 0;
    background: var(--color-panel); border: 1px solid var(--color-border);
    display: grid; place-items: center; font-size: 1.4rem;
  }
  &__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; }
  &__top { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); }
  &__level { font-size: 0.9rem; font-weight: 800; color: var(--color-text-1); }
  &__total { font-size: 0.74rem; color: var(--color-text-3); b { font-family: var(--font-mono); font-size: 0.95rem; color: var(--color-signal); } }
  &__bar { height: 6px; border-radius: 3px; background: var(--color-card); overflow: hidden; }
  &__bar-fill { height: 100%; background: linear-gradient(90deg, var(--color-signal), #C29250); border-radius: 3px; }
  &__next { font-size: 0.66rem; color: var(--color-text-4); }
  &__arrow { color: var(--color-text-4); font-size: 0.78rem; flex-shrink: 0; }
}

.acc-hero {
  display: flex; flex-direction: column; align-items: center; text-align: center;
  padding: var(--space-6) var(--page-padding-x) var(--space-4);
  &__avatar {
    width: 72px; height: 72px; border-radius: var(--radius-xl);
    background: linear-gradient(135deg, var(--color-signal, #E2B673), #C29250);
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-display); font-size: 1.7rem; font-weight: 800; color: #1a1410;
    margin-bottom: var(--space-3);
    box-shadow: 0 8px 22px -8px rgba(226,182,115,0.55);
  }
  &__name  { margin: 0 0 3px; font-size: 1.15rem; font-weight: 800; color: var(--color-text-1); }
  &__email { margin: 0; font-size: 0.76rem; color: var(--color-text-3); font-family: var(--font-mono); word-break: break-word; }
  &__cheer { margin: var(--space-3) 0 0; font-size: 0.85rem; color: var(--color-text-2); line-height: 1.5; max-width: 320px; }
}

.acc-stats {
  display: flex;
  background: var(--color-card); border: 1px solid var(--color-border);
  border-radius: var(--radius-lg); overflow: hidden;
  margin-bottom: var(--space-3);
  margin-left: var(--page-padding-x); margin-right: var(--page-padding-x);
}
.acc-stat {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  padding: var(--space-3) var(--space-2); gap: 4px; position: relative;
  background: none; border: none; cursor: pointer; font-family: var(--font-primary);
  &:not(:last-child)::after {
    content: ''; position: absolute; left: 0; top: 22%; bottom: 22%; width: 1px; background: var(--color-border);
  }
  &__value { font-family: var(--font-mono); font-size: 1.55rem; font-weight: 800; color: var(--color-text-1); line-height: 1; }
  &__label { font-size: 0.64rem; color: var(--color-text-3); text-align: center; line-height: 1.3; }
}

.acc-insights {
  display: flex; flex-direction: column; gap: var(--space-2);
  padding: 0 var(--page-padding-x); margin-bottom: var(--space-4);
}
.acc-insight {
  display: flex; align-items: center; gap: var(--space-2);
  padding: 10px var(--space-3);
  background: var(--color-raised); border: 1px solid var(--color-border);
  border-radius: var(--radius-md); font-size: 0.80rem; color: var(--color-text-2);
  &__icon { font-size: 1rem; }
  &__text strong { color: var(--color-signal); font-weight: 700; }
}

// Recently rated strip
.acc-rated {
  margin-bottom: var(--space-4);
  &__head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 var(--page-padding-x); margin-bottom: var(--space-2);
  }
  &__title { margin: 0; font-size: 0.82rem; font-weight: 800; color: var(--color-text-2); }
  &__more { background: none; border: none; color: var(--color-signal); font-family: var(--font-primary); font-size: 0.74rem; font-weight: 700; cursor: pointer; }
  &__row {
    display: flex; gap: var(--space-2); overflow-x: auto;
    padding: 0 var(--page-padding-x) var(--space-1);
    scrollbar-width: none; &::-webkit-scrollbar { display: none; }
  }
  &__card {
    flex-shrink: 0; width: 122px;
    background: var(--color-panel); border: 1px solid var(--color-border);
    border-radius: var(--radius-md); overflow: hidden; cursor: pointer;
    text-align: right; font-family: var(--font-primary);
    transition: border-color var(--transition-base);
    &:hover { border-color: var(--color-signal); }
  }
  &__img-wrap { width: 100%; height: 88px; background: var(--color-deep, #0b0907); }
  &__img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    &--ph { display: flex; align-items: center; justify-content: center; font-size: 1.6rem; }
  }
  &__info { padding: var(--space-2); display: flex; flex-direction: column; gap: 3px; }
  &__name {
    font-size: 0.76rem; font-weight: 700; color: var(--color-text-1);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__stars { display: flex; gap: 1px; color: var(--color-signal); font-size: 0.58rem;
    .fa-star-o { color: var(--color-border-lit); } }
  &__branch {
    font-size: 0.64rem; color: var(--color-text-4);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
}

.acc-nav {
  display: flex; flex-direction: column; gap: var(--space-1);
  padding: 0 var(--page-padding-x); margin-bottom: var(--space-4);
}
.acc-row {
  display: flex; align-items: center; gap: var(--space-3);
  width: 100%; padding: 13px var(--space-3);
  background: var(--color-panel); border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer; text-align: right; font-family: var(--font-primary);
  transition: border-color var(--transition-base);
  &:hover { border-color: var(--color-signal); }
  &__icon  { font-size: 1.05rem; flex-shrink: 0; }
  &__label { flex: 1; font-size: 0.88rem; font-weight: 600; color: var(--color-text-1); }
  &__hint  { font-family: var(--font-mono); font-size: 0.72rem; color: var(--color-text-4); }
  &__badge {
    min-width: 20px; height: 20px; padding: 0 6px; border-radius: var(--radius-full);
    background: var(--color-closed, #D86545); color: #fff;
    font-family: var(--font-mono); font-size: 0.66rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    &--warn { background: var(--color-signal); color: #1a1410; }
  }
  &__arrow { color: var(--color-text-4); font-size: 0.78rem; flex-shrink: 0; }
}

.acc-choice {
  display: flex; gap: var(--space-2);
  &__btn {
    flex: 1; padding: 11px; border-radius: var(--radius-lg);
    background: var(--color-card); border: 1px solid var(--color-border);
    color: var(--color-text-2); font-family: var(--font-primary);
    font-size: 0.88rem; font-weight: 700; cursor: pointer; transition: all 0.15s;
    &:hover { border-color: var(--color-signal); }
    &--on {
      background: var(--color-signal-soft); border-color: var(--color-signal);
      color: var(--color-signal); box-shadow: 0 0 0 1px var(--color-signal) inset;
    }
  }
}

.acc-theme {
  display: flex; align-items: center; gap: var(--space-3);
  padding: 0 var(--page-padding-x); margin-bottom: var(--space-4);
  &__label { font-size: 0.82rem; font-weight: 600; color: var(--color-text-2); white-space: nowrap; }
  &__btns  { display: flex; gap: var(--space-1); flex: 1; }
  &__btn {
    flex: 1; padding: 8px var(--space-2); border-radius: var(--radius-md);
    font-size: 0.76rem; font-weight: 600; border: 1px solid var(--color-border);
    background: var(--color-card); color: var(--color-text-3);
    cursor: pointer; font-family: var(--font-primary); transition: all var(--transition-base);
    &--active { border-color: var(--color-signal); background: var(--color-signal-soft); color: var(--color-signal); }
  }
}

.acc-logout {
  display: flex; align-items: center; gap: var(--space-2); justify-content: center;
  width: calc(100% - var(--page-padding-x) * 2);
  margin: 0 var(--page-padding-x) var(--space-4);
  padding: 11px var(--space-4); border-radius: var(--radius-md);
  border: 1px solid rgba(216,101,69,0.3); background: var(--color-closed-soft);
  color: var(--color-closed); font-family: var(--font-primary);
  font-size: 0.88rem; font-weight: 600; cursor: pointer; transition: all var(--transition-base);
  &:hover:not(:disabled) { background: rgba(216,101,69,0.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.acc-footer {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: var(--space-4) var(--page-padding-x) var(--space-6);
  &__brand   { font-family: var(--font-display); font-size: 1rem; font-weight: 800; color: var(--color-text-3); letter-spacing: 0.04em; }
  &__version { font-family: var(--font-mono); font-size: 0.68rem; color: var(--color-text-4); }
}

.acc-auth {
  max-width: 400px; margin: var(--space-6) auto; padding: 0 var(--page-padding-x);
  &__header   { text-align: center; margin-bottom: var(--space-5); }
  &__title    { margin: 0 0 var(--space-1); font-size: 1.2rem; font-weight: 800; color: var(--color-text-1); }
  &__subtitle { margin: 0; font-size: 0.82rem; color: var(--color-text-3); }
  &__toggle {
    display: flex; gap: var(--space-1); margin-bottom: var(--space-4);
    background: var(--color-card); border: 1px solid var(--color-border);
    border-radius: var(--radius-lg); padding: 4px;
  }
  &__toggle-btn {
    flex: 1; padding: 9px; border-radius: var(--radius-md); border: none;
    background: none; color: var(--color-text-3); font-family: var(--font-primary);
    font-size: 0.88rem; font-weight: 600; cursor: pointer; transition: all var(--transition-base);
    &--active { background: var(--color-signal); color: #1a1410; }
  }
  &__form   { display: flex; flex-direction: column; gap: var(--space-3); }
  &__terms  {
    margin: 0; font-size: 0.75rem; color: var(--color-text-4); text-align: center; line-height: 1.6;
    a { color: var(--color-signal); text-decoration: none; font-weight: 600; &:hover { text-decoration: underline; } }
  }
  &__submit {
    display: flex; align-items: center; justify-content: center; gap: var(--space-2);
    width: 100%; padding: 13px; border-radius: var(--radius-md); border: none;
    background: linear-gradient(135deg, var(--color-signal, #E2B673), #C29250);
    color: #1a1410; font-family: var(--font-primary); font-size: 0.95rem; font-weight: 800;
    cursor: pointer; transition: opacity var(--transition-base); margin-top: var(--space-2);
    &:hover:not(:disabled) { opacity: 0.9; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}

.acc-field {
  display: flex; flex-direction: column; gap: var(--space-1);
  &__label { font-size: 0.80rem; font-weight: 600; color: var(--color-text-2); }
  &__input {
    background: var(--color-card); border: 1px solid var(--color-border-lit);
    border-radius: var(--radius-md); padding: 11px var(--space-3);
    color: var(--color-text-1); font-family: var(--font-primary); font-size: 0.88rem;
    outline: none; width: 100%; transition: border-color var(--transition-base);
    &:focus { border-color: var(--color-signal); }
    &::placeholder { color: var(--color-text-4); }
  }
}

.acc-error {
  display: flex; align-items: center; gap: var(--space-2);
  max-width: 560px; margin: var(--space-3) auto;
  margin-left: var(--page-padding-x); margin-right: var(--page-padding-x);
  padding: var(--space-3) var(--space-4); border-radius: var(--radius-md);
  background: var(--color-closed-soft); border: 1px solid rgba(216,101,69,0.3);
  color: var(--color-closed); font-size: 0.82rem; font-weight: 500;
}
</style>